import React, { useState, useRef, useEffect } from 'react';

import { Input, CloseButton, BaseButton, useT } from '@repo/common';
import DegreeList from './DegreeList';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';

const EMPTY = {
  schoolName: '',
  fieldOfStudy: '',
  degree: '',
  studyPeriod: '',
};

const EducationFullPage = ({ initialData, onClose, onSave }) => {
  const t = useT();
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(initialData || {}),
  }));
  const [openDegree, setOpenDegree] = useState(false);
  const [errors, setErrors] = useState({});

  const schoolRef = useRef(null);
  const degreeWrapRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => schoolRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!openDegree) return;
    const handleClickOutside = (e) => {
      if (degreeWrapRef.current && !degreeWrapRef.current.contains(e.target)) {
        setOpenDegree(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDegree]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAdd = () => {
    onSave(form);
  };

  return (
    <div className='fixed inset-0 z-[60] flex flex-col bg-grayscale-gray00'>
      {/* Header */}
      <div className='relative shrink-0 h-[54px] px-4 py-3 flex items-center justify-between border-b border-grayscale-gray02'>
        <CloseButton onClick={onClose} />
        <span className='typo-b16Strong text-text-primary'>
          {t('Education', '학력')}
        </span>
        <div className='size-6' />
      </div>

      {/* Body */}
      <div className='flex-1 overflow-y-auto px-4 pt-[30px] pb-[160px] flex flex-col gap-4'>
        {/* School name */}
        <div>
          <Input
            ref={schoolRef}
            label={t('School name', '학교명')}
            placeholder={t('e.g. Seoul National University', '예: 서울대학교')}
            value={form.schoolName}
            onChange={(e) => setField('schoolName', e.target.value)}
            hideCount
          />
        </div>

        {/* Field of study */}
        <Input
          label={t('Field of study', '전공')}
          placeholder={t('e.g. Design', '예: 디자인')}
          value={form.fieldOfStudy}
          onChange={(e) => setField('fieldOfStudy', e.target.value)}
          hideCount
        />

        {/* Degree */}
        <div ref={degreeWrapRef} className='relative'>
          <div onClick={() => setOpenDegree((v) => !v)}>
            <Input
              label={t('Degree', '학위')}
              placeholder={t('e.g. Bachelor’s, Master’s', '예: 학사, 석사')}
              value={form.degree}
              readOnly
              hideCount
              rightIcon={openDegree ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            />
          </div>
          {openDegree && (
            <div className='relative w-full mt-1.5 z-10'>
              <DegreeList
                onSelect={(deg) => {
                  setField('degree', deg);
                  setOpenDegree(false);
                }}
              />
            </div>
          )}
        </div>

        {/* Study period */}
        <Input
          label={t('Study period', '재학 기간')}
          placeholder={t('e.g. 2018.03 - Present', '예: 2018.03 - 현재')}
          value={form.studyPeriod}
          onChange={(e) => setField('studyPeriod', e.target.value)}
          hideCount
        />
      </div>

      <div className='shrink-0 p-4 border-t border-grayscale-gray02'>
        <BaseButton onClick={handleAdd} size='l' hideIcon width='100%'>
          {t('Add', '추가')}
        </BaseButton>
      </div>
    </div>
  );
};

export default EducationFullPage;
